import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { AED } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

const MIN = 99;
const MAX_TOPUP = 2000;
const r2 = (n) => Math.round(n * 100) / 100;

export default function TopUp() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const balance = r2(user?.deposit_balance || 0);
  const shortfall = Math.max(0, r2(MIN - balance));
  // Minimum: the shortfall to AED 99 (includes any negative balance); AED 10 once at/above the minimum.
  const minTopup = balance >= MIN ? 10 : shortfall;
  const [amount, setAmount] = useState(String(minTopup));
  const [busy, setBusy] = useState(false);
  const amt = r2(parseFloat(amount) || 0);
  const tooLow = amt < minTopup;
  const tooHigh = amt > MAX_TOPUP;
  const invalid = tooLow || tooHigh;
  const error = tooLow ? `Minimum top-up is ${AED(minTopup)}` : tooHigh ? `Maximum top-up is ${AED(MAX_TOPUP)} per top-up` : "";

  const pay = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/topup", { origin_url: window.location.origin, amount: amt });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-12 sm:px-6" data-testid="topup-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-8">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Wallet className="h-7 w-7" /></div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Top Up Security Deposit</h1>
        <div className="mt-4 space-y-1 text-sm text-slate-600">
          <p>Current balance: <span className="font-semibold text-slate-900" data-testid="topup-current">{AED(balance)}</span></p>
          <p>Minimum required: <span className="font-semibold text-slate-900">{AED(MIN)}</span></p>
          {shortfall > 0 && <p>Shortfall to reach minimum: <span className="font-semibold text-red-600" data-testid="topup-shortfall">{AED(shortfall)}</span></p>}
          <p>Minimum top-up: <span className="font-semibold text-slate-900" data-testid="topup-min">{AED(minTopup)}</span> · Maximum: <span className="font-semibold text-slate-900">{AED(MAX_TOPUP)}</span></p>
        </div>
        <div className="mt-5">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Amount to top up (editable)</label>
          <Input type="number" min={minTopup} max={MAX_TOPUP} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount" className={`rounded-lg ${invalid ? "border-red-400 focus:border-red-400" : ""}`} />
          {error && <p className="mt-1 text-xs font-medium text-red-600" data-testid="topup-error">{error}</p>}
          <p className="mt-2 text-sm text-slate-700">Amount to pay: <span className="font-bold" data-testid="topup-payable">{AED(invalid ? 0 : amt)}</span></p>
          <p className="mt-1 text-xs text-slate-400">New balance after top up: <span data-testid="topup-new-balance">{AED(invalid ? balance : r2(balance + amt))}</span></p>
        </div>
        <Button data-testid="topup-confirm" disabled={busy || invalid} onClick={pay} className="mt-6 w-full rounded-full bg-primary py-6 text-base font-semibold hover:bg-primary/90">{busy ? "Processing…" : `Pay ${AED(invalid ? minTopup : amt)}`}</Button>
      </div>
    </div>
  );
}
